import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownRight, ShoppingCart, DollarSign, FileWarning, CheckCircle2, XCircle, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const Kpi = ({ label, value, currency, hint, accent = "slate", icon: Icon, testId }) => {
  const accentMap = {
    slate: "bg-slate-50 text-slate-700",
    green: "bg-emerald-50 text-emerald-600",
    red: "bg-rose-50 text-rose-600",
    blue: "bg-sky-50 text-sky-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <Card className="p-5 border border-slate-200 shadow-sm rounded-xl bg-white" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        {Icon && <div className={`w-8 h-8 rounded-lg grid place-items-center ${accentMap[accent]}`}><Icon size={16} /></div>}
      </div>
      <div className="flex items-baseline gap-2">
        <span className={`text-2xl sm:text-3xl font-bold font-mono tabular-nums tracking-tight ${
          accent === "green" ? "text-emerald-600" : accent === "red" ? "text-rose-600" : "text-slate-900"
        }`}>{value}</span>
        {currency && <span className="text-sm font-semibold text-slate-500">{currency}</span>}
      </div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </Card>
  );
};

function OperatorDashboard({ data }) {
  const { isOpMayor } = useAuth();
  const market = isOpMayor ? "mayor" : "detalle";
  const c = data.corrections_count || {};
  const stats = data.market_stats || {};
  return (
    <div className="space-y-8" data-testid="operator-dashboard">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Panel del Operador</h1>
        <p className="text-slate-500 mt-1 capitalize">Mercado {market} — acciones rápidas</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to={`/transaccion?op=compra&market=${market}`}>
          <Card className="p-8 border border-slate-200 rounded-2xl bg-white hover:border-emerald-500 hover:shadow transition-all cursor-pointer" data-testid="quick-compra">
            <ShoppingCart className="text-emerald-600 mb-3" size={28} />
            <div className="text-xl font-bold text-slate-900">Registrar Compra</div>
            <p className="text-sm text-slate-500 mt-1 capitalize">Compra al {market}</p>
          </Card>
        </Link>
        <Link to={`/transaccion?op=venta&market=${market}`}>
          <Card className="p-8 border border-slate-200 rounded-2xl bg-white hover:border-rose-500 hover:shadow transition-all cursor-pointer" data-testid="quick-venta">
            <DollarSign className="text-rose-600 mb-3" size={28} />
            <div className="text-xl font-bold text-slate-900">Registrar Venta</div>
            <p className="text-sm text-slate-500 mt-1 capitalize">Venta al {market}</p>
          </Card>
        </Link>
      </div>

      <section>
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Balance disponible para vender</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Kpi testId={`operator-balance-${market}`} label={`Saldo USDT ${market}`} value={fmt.usdt(stats.balance_usdt || 0)} currency="USDT" icon={Wallet} accent="blue" />
          <Kpi label="Total comprado" value={fmt.usdt(stats.total_bought_usdt || 0)} currency="USDT" icon={ArrowDownRight} />
          <Kpi label="Total vendido" value={fmt.usdt(stats.total_sold_usdt || 0)} currency="USDT" icon={ArrowUpRight} />
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Estado de mis solicitudes de corrección</h2>
        <div className="grid grid-cols-3 gap-3">
          <Kpi testId="kpi-corr-pend" label="Pendientes" value={c.pendiente || 0} icon={Clock} accent="amber" />
          <Kpi testId="kpi-corr-aprob" label="Aprobadas" value={c.aprobada || 0} icon={CheckCircle2} accent="green" />
          <Kpi testId="kpi-corr-rech" label="Rechazadas" value={c.rechazada || 0} icon={XCircle} accent="red" />
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Transacciones recientes del mercado</h2>
        <Card className="border border-slate-200 rounded-xl bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                <th className="px-4 py-3 text-left font-semibold">Op</th>
                <th className="px-4 py-3 text-left font-semibold">Moneda</th>
                <th className="px-4 py-3 text-right font-semibold">USDT</th>
                <th className="px-4 py-3 text-left font-semibold">Operador</th>
                <th className="px-4 py-3 text-left font-semibold">Estado</th>
              </tr>
            </thead>
            <tbody className="font-mono tabular-nums">
              {(data.recent_transactions || []).length === 0 && (
                <tr><td colSpan={6} className="text-center py-6 text-slate-400">Sin transacciones aun</td></tr>
              )}
              {(data.recent_transactions || []).map((t) => (
                <tr key={t.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-600">{fmt.date(t.transaction_date)}</td>
                  <td className="px-4 py-2 capitalize">{t.operation_type}</td>
                  <td className="px-4 py-2 font-semibold">{t.currency}</td>
                  <td className="px-4 py-2 text-right">{fmt.usdt(t.usdt_amount)}</td>
                  <td className="px-4 py-2 font-sans text-slate-600">{t.created_by_name || "-"}</td>
                  <td className="px-4 py-2 capitalize">{t.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </section>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const { canSeeFinancial, logout } = useAuth();

  const loadDashboard = useCallback(async () => {
    setLoadError("");
    try {
      const response = await api.get("/dashboard");
      const nextData = response.data;
      const hasExpectedData = nextData?.limited || (nextData?.detalle && nextData?.mayor);
      if (!hasExpectedData) throw new Error("Respuesta incompleta del dashboard");
      setData(nextData);
    } catch (error) {
      if (error.response?.status === 401) {
        await logout();
        return;
      }
      setLoadError("No se pudo cargar el dashboard. Verifica que el servidor local este activo.");
    }
  }, [logout]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  if (loadError) {
    return (
      <div className="flex items-center gap-3 text-rose-600" role="alert">
        <span>{loadError}</span>
        <Button variant="outline" onClick={loadDashboard}>Reintentar</Button>
      </div>
    );
  }
  if (!data) return <div className="text-slate-500" data-testid="dashboard-loading">Cargando...</div>;

  if (data.limited) return <OperatorDashboard data={data} />;

  const det = data.detalle;
  const may = data.mayor;
  const ms = data.major_summary;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-slate-500 mt-1">Resumen general — Mercado Detalle &amp; Mayor</p>
        </div>
        <div className="flex gap-2">
          <Link to="/compra-venta?op=compra"><Button data-testid="quick-buy" className="bg-emerald-600 hover:bg-emerald-700">+ Compra</Button></Link>
          <Link to="/compra-venta?op=venta"><Button data-testid="quick-sell" variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50">- Venta</Button></Link>
        </div>
      </div>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-6 bg-slate-900 rounded-full" />
          <h2 className="text-lg font-semibold text-slate-800">Mercado Detalle</h2>
          <span className="text-xs text-slate-400">/ {det.period_mode === "mensual" ? `Actividad ${det.period_year}-${String(det.period_month).padStart(2, "0")}` : "Acumulado pendiente de cierre"}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi testId="kpi-balance-detalle" label="Saldo Disponible" value={fmt.usdt(det.balance_usdt)} currency="USDT" accent="blue" icon={Wallet} hint={`Promedio compra: ${fmt.dop(det.avg_buy_price)} DOP`} />
          <Kpi testId="kpi-comprado-detalle" label="Total Comprado" value={fmt.usdt(det.total_bought_usdt)} currency="USDT" icon={ArrowDownRight} hint={`Invertido: ${fmt.dop(det.total_invested)} DOP`} />
          <Kpi testId="kpi-vendido-detalle" label="Total Vendido" value={fmt.usdt(det.total_sold_usdt)} currency="USDT" icon={ArrowUpRight} hint={`Recibido: ${fmt.dop(det.total_received)} DOP`} />
          <Kpi testId="kpi-pl-detalle" label="Ganancia / Pérdida" value={fmt.dop(det.profit_loss)} currency="DOP" accent={det.profit_loss >= 0 ? "green" : "red"} icon={det.profit_loss >= 0 ? TrendingUp : TrendingDown} hint={`Beneficio por USDT: ${fmt.dop(det.benefit_per_usdt)} DOP`} />
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-6 bg-slate-900 rounded-full" />
          <h2 className="text-lg font-semibold text-slate-800">Mercado Mayor — Inventario</h2>
          <span className="text-xs text-slate-400">{may.period_mode === "mensual" ? `${may.period_year}-${String(may.period_month).padStart(2, "0")}` : "Acumulado pendiente de cierre"}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi testId="kpi-balance-mayor" label="Saldo Disponible" value={fmt.usdt(may.balance_usdt)} currency="USDT" accent="blue" icon={Wallet} hint={`Costo % promedio: ${fmt.pct(may.avg_buy_pct)}`} />
          <Kpi testId="kpi-comprado-mayor" label="Total Comprado" value={fmt.usdt(may.total_bought_usdt)} currency="USDT" icon={ArrowDownRight} />
          <Kpi testId="kpi-vendido-mayor" label="Total Vendido" value={fmt.usdt(may.total_sold_usdt)} currency="USDT" icon={ArrowUpRight} />
          <Kpi testId="kpi-pl-mayor" label="G/P Bruta (acumulado)" value={fmt.usdt(may.gross_profit_loss)} currency="USD" accent={may.gross_profit_loss >= 0 ? "green" : "red"} icon={may.gross_profit_loss >= 0 ? TrendingUp : TrendingDown} hint={`Beneficio promedio: ${fmt.pct(may.benefit_avg_pct)}`} />
        </div>
      </section>

      {ms && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-6 bg-slate-900 rounded-full" />
            <h2 className="text-lg font-semibold text-slate-800">Mercado Mayor — Capital del Mes</h2>
            <span className="text-xs text-slate-400">{ms.year}-{String(ms.month).padStart(2,"0")}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Kpi testId="kpi-initial-cash" label="Saldo inicial USD" value={fmt.usdt(ms.initial_cash_usd)} currency="USD" icon={Wallet} />
            <Kpi testId="kpi-payments" label="Pagos/Gastos del mes" value={fmt.usdt(ms.total_payments)} currency="USD" accent="amber" icon={FileWarning} />
            <Kpi testId="kpi-gross-profit" label="Beneficio bruto del mes" value={fmt.usdt(ms.gross_profit)} currency="USD" accent={ms.gross_profit >= 0 ? "green" : "red"} icon={ms.gross_profit >= 0 ? TrendingUp : TrendingDown} />
            <Kpi testId="kpi-general-balance" label="Balance general" value={fmt.usdt(ms.general_balance)} currency="USD" />
            <Kpi testId="kpi-cash-usd" label="Efectivo USD disponible" value={fmt.usdt(ms.available_cash_usd)} currency="USD" accent="green" />
            <Kpi testId="kpi-capital" label="Capital estimado disponible" value={fmt.usdt(ms.estimated_available_capital)} currency="USD" accent="blue" icon={TrendingUp} hint={`Saldo USDT: ${fmt.usdt(ms.available_usdt_balance)} × ${fmt.usdt(ms.avg_buy_unit_overall)}`} />
          </div>
        </section>
      )}
    </div>
  );
}
